import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import Invoice from "../../../model/invoice/Invoice";
import {Dispatch} from "react-redux";
import {api} from "../../../api/api";
import {INVOICE_EVENTS_FETCHED, INVOICE_FETCHED, INVOICE_PAYMENTS_FETCHED, INVOICE_UPDATED} from "./actionType";
import {Option} from "ts-option";
import InvoicePartsAndLabourDetails from "../../../model/invoice/InvoicePartsAndLabourDetails";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {Action} from "redux-actions";
import InvoiceEvent, {InvoiceEventType} from "../../../model/invoice/event/InvoiceEvent";
import Payment from "../../../model/payment/Payment";

/**
 * Invoice fetched successful action.
 * @param {string} id job identifier.
 * @param {Invoice} invoice
 * @return {Action<INVOICE_FETCHED>}
 */
function invoiceFetched(id: string, invoice: Invoice) {
	return {type: INVOICE_FETCHED, payload: {jobId: id, invoice}}
}

/**
 * Invoice updated successful action.
 * @param {string} id job identifier.
 * @param {Invoice} invoice
 * @return {Action<INVOICE_UPDATED>}
 */
function invoiceUpdated(id: string, invoice: Invoice) {
	return {type: INVOICE_UPDATED, payload: {jobId: id, invoice}}
}

/**
 * Invoice events fetched successful action.
 * @param invoiceId
 * @param {Array<InvoiceEvent>} events invoice events.
 * @return {Action<INVOICE_EVENTS_FETCHED>}
 */
export function invoiceEventsFetched(invoiceId: string, events: Array<InvoiceEvent>): Action<INVOICE_EVENTS_FETCHED> {
	return {type: INVOICE_EVENTS_FETCHED, payload: {invoiceId, events}};
}

/**
 * Invoice payments fetched successful action.
 * @param invoiceId
 * @param {Array<InvoiceEvent>} payments invoice payments.
 * @return {Action<INVOICE_PAYMENTS_FETCHED>}
 */
export function invoicePaymentsFetched(invoiceId: string, payments: Array<Payment>): Action<INVOICE_PAYMENTS_FETCHED> {
	return {type: INVOICE_PAYMENTS_FETCHED, payload: {invoiceId, payments}};
}

/**
 * Fetch invoice action.
 * @param {string} jobId job identifier.
 * @param {OnSuccess} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure action callback.
 */
export function fetchInvoice(jobId: string, onSuccess: OnSuccessData<Invoice>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.invoiceApi.fetchInvoice(jobId).then(invoice => {
			dispatch(invoiceFetched(jobId, invoice));
			onSuccess(invoice);
		}).catch((error) => {
			if (error && error.response && error.response.status !== 404) {
				notifyBugsnagAndShowToast(error, "Fetch invoice")
			}
			onFailure(error);
		});
	}
}

/**
 * Update invoice action.
 * @param {string} jobId job identifier.
 * @param {Invoice} invoice invoice for update.
 * @param {Option<InvoicePartsAndLabourDetails>} labourAndPartsDetails updated invoice adjust data.
 * @param {OnSuccess} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure action callback.
 */
export function updateInvoice(jobId: string,
	invoice: Invoice,
	labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>,
	onSuccess: OnSuccessData<Invoice>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.invoiceApi.updateInvoice(invoice.id, invoice, labourAndPartsDetails).then(invoice => {
			dispatch(invoiceUpdated(jobId, invoice));
			onSuccess(invoice);
		}).catch(onFailure);
	}
}

/**
 * Approve invoice action.
 * @param {string} jobId job identifier.
 * @param {string} invoiceId invoice identifier.
 * @param {OnSuccessData<Invoice>} onSuccess on invoice approve success callback.
 * @param {OnFailure} onFailure on invoice approve failure callback.
 */
export function approveInvoice(jobId: string, invoiceId: string, onSuccess: OnSuccessData<Invoice>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.invoiceApi.approveInvoice(invoiceId).then(invoice => {
			dispatch(invoiceUpdated(jobId, invoice));
			onSuccess(invoice);
		}).catch(onFailure);
	}
}

/**
 * Fetch invoice events action.
 * @param invoiceId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchInvoiceEvents(invoiceId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.invoiceApi.fetchInvoiceEvents(invoiceId).then(
			(events: Array<InvoiceEventType>) => {
				dispatch(invoiceEventsFetched(invoiceId, events));
				onSuccess();
			}).catch(onFailure);
	}
}

/**
 * Fetch invoice payments action.
 * @param invoiceId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchInvoicePayments(invoiceId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.invoicePaymentApi.fetchInvoicePayments(invoiceId).then(
			(events: Array<Payment>) => {
				dispatch(invoicePaymentsFetched(invoiceId, events));
				onSuccess();
			}).catch(onFailure);
	}
}
